import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { projects } from '../data/projects';
import { useTheme } from '../contexts/ThemeContext';

// Define project categories
const categories = [
  'All',
  'Medical AI',
  'Bioinformatics',
  'Machine Learning',
  'Engineering'
];

const getProjectCategories = (technologies: string[]) => {
  const cats: string[] = [];
  if (technologies.some(tech => ['Chemical Engineering', 'Control Systems', '3D Printing', 'Robotics', 'Kinematics', 'CAD', 'Embedded Systems', 'Hardware Design', 'Electronics', 'SolidWorks', 'MATLAB'].includes(tech))) {
    cats.push('Engineering');
  }
  if (technologies.some(tech => ['Medical Imaging', 'SonoSAM', 'Ultrasound Analysis', 'Biomedical Engineering', 'Healthcare', 'Medical AI'].includes(tech)) || (technologies.includes('Embedded Systems') && technologies.includes('IoT'))) {
    cats.push('Medical AI');
  }
  if (technologies.some(tech => ['Bioinformatics', 'RNA-seq', 'Gene Expression', 'Cancer Genomics'].includes(tech))) {
    cats.push('Bioinformatics');
  }
  if (technologies.some(tech => ['Machine Learning', 'Deep Learning', 'Neural Networks', 'AI', 'Transformers', 'LightGBM'].includes(tech))) {
    cats.push('Machine Learning');
  }
  // Hardcode specific overrides based on project categories requested
  if (technologies.includes('Swift') && technologies.includes('iOS') && technologies.includes('Supabase')) {
    if (!cats.includes('Engineering')) cats.push('Engineering');
  }
  if (cats.length === 0) cats.push('All');
  return cats;
};

const Projects = () => {
  const { isDarkMode } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredProjects = useMemo(() => {
    if (selectedCategory === 'All') {
      return projects;
    }
    return projects.filter(project => getProjectCategories(project.technologies).includes(selectedCategory));
  }, [selectedCategory]);

  return (
    <div className={`min-h-screen py-20 md:py-32 ${isDarkMode ? 'bg-black' : 'bg-stone-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Title */}
          <h1 className={`font-serif text-4xl md:text-5xl font-normal mb-8 md:mb-12 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>
            Projects
          </h1>

          {/* Category Filter */}
          <div className="mb-12 flex gap-4 md:gap-8 overflow-x-auto pb-4 border-b border-stone-200 dark:border-gray-700">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`text-base md:text-lg whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? isDarkMode
                      ? 'text-white border-b-2 border-white pb-1'
                      : 'text-stone-900 border-b-2 border-stone-900 pb-1'
                    : isDarkMode
                    ? 'text-gray-400 hover:text-gray-200'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
                style={{ fontFamily: 'Georgia, serif' }}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredProjects.map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="block"
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className={`h-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${
                    isDarkMode ? 'border-gray-700' : 'border-stone-200'
                  } p-6 rounded-lg`}
                >
                  <h3 className={`font-serif text-xl md:text-2xl font-normal mb-3 ${
                    isDarkMode ? 'text-white' : 'text-stone-900'
                  }`}>
                    {project.title}
                  </h3>
                  <p className={`font-serif text-sm md:text-base ${
                    isDarkMode ? 'text-gray-300' : 'text-stone-500'
                  } mb-4`}>
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className={`text-sm px-3 py-1 ${
                          isDarkMode
                            ? 'bg-gray-700 text-gray-200'
                            : 'bg-stone-100 text-stone-700'
                        } rounded-full`}
                        style={{ fontFamily: 'Georgia, serif' }}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    {project.metrics.map((metric) => (
                      <div key={metric.label}>
                        <div className={`font-serif text-base md:text-lg font-normal ${
                          isDarkMode ? 'text-white' : 'text-stone-900'
                        }`}>
                          {metric.value}
                        </div>
                        <div className={`font-serif text-xs md:text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-stone-500'
                        }`}>
                          {metric.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Projects;